/**
 * Gate I8 Phase 3: Workflow Dashboard Grid Component
 * 
 * Displays a 5×4 matrix (Track × Eval Status) with counts per cell.
 * Fixed row/column ordering; no dynamic sorting.
 */

import { Card } from "@/components/ui/card";
import { 
  VALID_TRACKS, 
  VALID_EVAL_STATUSES,
  TRACK_LABELS,
  EVAL_STATUS_LABELS,
  buildBucketKey,
  type Track,
  type EvalStatus,
  type BucketKey
} from "@/lib/api/workflow-types";

interface WorkflowDashboardGridProps {
  bucketCounts: Record<string, number>;
  selectedBucket: BucketKey | null;
  onBucketSelect: (bucketKey: BucketKey) => void;
}

export function WorkflowDashboardGrid({
  bucketCounts,
  selectedBucket,
  onBucketSelect,
}: WorkflowDashboardGridProps) {
  return (
    <div className="overflow-x-auto" data-testid="workflow-grid">
      <table className="w-full border-collapse table-zebra">
        <thead>
          <tr>
            <th className="p-2 text-left text-sm font-medium text-muted-foreground w-[100px] min-w-[80px]">
              Status
            </th>
            {VALID_EVAL_STATUSES.map((status) => (
              <th 
                key={status} 
                className="p-2 text-center text-sm font-medium text-muted-foreground min-w-[100px]"
              >
                {EVAL_STATUS_LABELS[status]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {VALID_TRACKS.map((track) => (
            <tr key={track} className="border-t border-border">
              <td className="p-2 text-sm font-medium">
                {TRACK_LABELS[track]}
              </td>
              {VALID_EVAL_STATUSES.map((status) => (
                <BucketCell
                  key={`${track}-${status}`}
                  track={track}
                  evalStatus={status}
                  count={bucketCounts[buildBucketKey(track, status)] ?? 0}
                  isSelected={selectedBucket === buildBucketKey(track, status)}
                  onSelect={() => onBucketSelect(buildBucketKey(track, status))}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface BucketCellProps {
  track: Track;
  evalStatus: EvalStatus;
  count: number;
  isSelected: boolean;
  onSelect: () => void;
}

function BucketCell({ track, evalStatus, count, isSelected, onSelect }: BucketCellProps) {
  const bucketKey = buildBucketKey(track, evalStatus);
  
  return (
    <td className="p-1">
      <Card
        className={`
          cursor-pointer p-3 text-center transition-all hover-elevate
          ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}
          ${count === 0 ? 'opacity-50' : ''}
        `}
        onClick={onSelect}
        data-testid={`workflow-cell-${track}-${evalStatus}`}
      >
        <div className="text-2xl font-bold tabular-nums">
          {count}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {EVAL_STATUS_LABELS[evalStatus]}
        </div>
      </Card>
    </td>
  );
}

export default WorkflowDashboardGrid;
